import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { Survey } from '../types';

interface SurveyContextType {
  surveys: Survey[];
  allSurveys: Survey[];
  loading: boolean;
  error: string | null;
  addSurvey: (survey: Omit<Survey, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => Promise<void>;
  updateSurvey: (id: number, survey: Partial<Survey>) => Promise<void>;
  deleteSurvey: (id: number) => Promise<void>;
  getSurvey: (id: number) => Survey | undefined;
  incrementViews: (id: number) => Promise<void>;
  refreshSurveys: () => Promise<void>;
}

const SurveyContext = createContext<SurveyContextType | undefined>(undefined);

export const SurveyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [allSurveys, setAllSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('surveys')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const allData = data || [];
      setAllSurveys(allData);
      setSurveys(allData.filter(s => s.is_published !== false));
    } catch (err) {
      console.error('Error fetching surveys:', err);
      setError(err instanceof Error ? err.message : '설문조사를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveys();
  }, []);

  const addSurvey = async (surveyData: Omit<Survey, 'id' | 'view_count' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);
      const { error: insertError } = await supabase
        .from('surveys')
        .insert({
          title: surveyData.title,
          content: surveyData.content,
          is_published: surveyData.is_published ?? true,
          author: surveyData.author || '관리자',
          view_count: 0
        });

      if (insertError) throw insertError;
      await fetchSurveys();
    } catch (err) {
      console.error('Error adding survey:', err);
      setError(err instanceof Error ? err.message : '설문조사 추가에 실패했습니다.');
      throw err;
    }
  };

  const updateSurvey = async (id: number, surveyData: Partial<Survey>) => {
    try {
      setError(null);
      const updateData: any = {};
      if (surveyData.title !== undefined) updateData.title = surveyData.title;
      if (surveyData.content !== undefined) updateData.content = surveyData.content;
      if (surveyData.is_published !== undefined) updateData.is_published = surveyData.is_published;
      if (surveyData.author !== undefined) updateData.author = surveyData.author;

      const { error: updateError } = await supabase
        .from('surveys')
        .update(updateData)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchSurveys();
    } catch (err) {
      console.error('Error updating survey:', err);
      setError(err instanceof Error ? err.message : '설문조사 수정에 실패했습니다.');
      throw err;
    }
  };

  const deleteSurvey = async (id: number) => {
    try {
      setError(null);
      const { error: deleteError } = await supabase
        .from('surveys')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      setSurveys(prev => prev.filter(s => s.id !== id));
      setAllSurveys(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting survey:', err);
      setError(err instanceof Error ? err.message : '설문조사 삭제에 실패했습니다.');
      throw err;
    }
  };

  const incrementViews = async (id: number) => {
    try {
      const survey = allSurveys.find(s => s.id === id);
      if (survey) {
        const { error: updateError } = await supabase
          .from('surveys')
          .update({ view_count: survey.view_count + 1 })
          .eq('id', id);

        if (updateError) throw updateError;

        const updateList = (list: Survey[]) =>
          list.map(s => s.id === id ? { ...s, view_count: s.view_count + 1 } : s);

        setSurveys(updateList);
        setAllSurveys(updateList);
      }
    } catch (err) {
      console.error('Error incrementing views:', err);
    }
  };

  const getSurvey = (id: number) => {
    return allSurveys.find(s => s.id === id);
  };

  const refreshSurveys = async () => {
    await fetchSurveys();
  };

  return (
    <SurveyContext.Provider value={{
      surveys,
      allSurveys,
      loading,
      error,
      addSurvey,
      updateSurvey,
      deleteSurvey,
      getSurvey,
      incrementViews,
      refreshSurveys
    }}>
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurveys = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurveys must be used within a SurveyProvider');
  }
  return context;
};
